import { request } from '@/api/apiUtils';
import { commentsEntity } from '@/data/entities/comments';
import { getPrefixes, useCustomMutation } from '@/data/mutations';
import type { CommentType, IComment, ResponseType } from '@/types';

interface DeleteCommentParams {
    project_id: number;
    comment_id?: number;
    update_text_atom?: string;
    id?: string[];
}

const commentDeletePrefixes = getPrefixes('comment', 'delete');
const commentCreatePrefixes = getPrefixes('comment', 'create');
const commentUpdatePrefixes = getPrefixes('comment', 'update');

export function useDeleteComment(parentId?: number) {
    return useCustomMutation<DeleteCommentParams, ResponseType<IComment>>({
        meta: parentId ? { syncQueryKey: commentsEntity.fetchers.detail.key({ id: parentId }) } : undefined,
        mutationFn: async params => {
            const searchParams = new URLSearchParams();
            if (params.update_text_atom) searchParams.set('update_text_atom', params.update_text_atom);
            if (params.id) searchParams.set('id', JSON.stringify(params.id));
            const qs = searchParams.toString();

            const urlBase = `projects/${params.project_id}/comments${params.comment_id ? `/${params.comment_id}` : ''}`;
            const url = `${urlBase}${qs ? `?${qs}` : ''}`;

            return request<ResponseType<IComment>>(url, {
                method: 'DELETE',
                body: JSON.stringify(params),
            });
        },
        invalidate: commentDeletePrefixes,
    });
}

export function useCreateComment() {
    return useCustomMutation<CommentType, ResponseType<IComment>>({
        mutationFn: async params =>
            request<ResponseType<IComment>>(`projects/${params.project_id}/comments`, {
                method: 'POST',
                body: JSON.stringify(params),
            }),
        invalidate: commentCreatePrefixes,
    });
}

export function useCreateChildComment(parentId?: number) {
    // Targeted invalidation for the specific thread, not the full comments tree.
    return useCustomMutation<{ project_id: number; parent_id: number; text: string }, ResponseType<IComment>>({
        meta: parentId ? { syncQueryKey: commentsEntity.fetchers.detail.key({ id: parentId }) } : undefined,
        mutationFn: async params =>
            request<ResponseType<IComment>>(`projects/${params.project_id}/comments`, {
                method: 'POST',
                body: JSON.stringify(params),
            }),
        invalidate: [commentsEntity.fetchers.detail.key({ id: parentId ?? 0 })],
    });
}

export function useUpdateComment(parentId?: number) {
    return useCustomMutation<{ project_id: number; comment_id: number; data: { [key: string]: string } }, ResponseType<IComment>>({
        meta: parentId ? { syncQueryKey: commentsEntity.fetchers.detail.key({ id: parentId }) } : undefined,
        mutationFn: async params =>
            request<ResponseType<IComment>>(`projects/${params.project_id}/comments/${params.comment_id}?_method=PUT`, {
                method: 'POST',
                body: JSON.stringify(params.data),
            }),
        invalidate: commentUpdatePrefixes,
    });
}

export function useSubscribeThread() {
    return useCustomMutation<{ project_id: number; comment_id: number }, ResponseType<IComment>>({
        mutationFn: async params =>
            request<ResponseType<IComment>>(`projects/${params.project_id}/comments/${params.comment_id}/subscribe`, {
                method: 'POST',
            }),
        invalidate: [],
    });
}
